<template>
  <v-row>
    <v-col cols="12">
      <v-card flat class="rounded-20">
        <v-card-text class="black--text pb-0">
          <p>To continue with abutment export, confirm the choices below.</p>
          Choose the desired Scan Body to match your desired library for
          geometry and design purposes.
        </v-card-text>
      </v-card>
    </v-col>

    <v-col cols="12">
      <v-card class="rounded-20" flat>
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              v-model="scanBodyComponentId"
              label="Scan Body Library for Export"
              :items="mappedScanBodies"
              outlined
              dense
              hide-details
              class="mx-4"
              :menu-props="{ rounded: '10' }"
            />
          </v-col>

          <v-col cols="12" class="d-flex align-center">
            <v-switch
              inset
              v-model="enableHealingCap"
              hide-details
              class="mt-0 py-2 ml-6"
            />
            <v-autocomplete
              v-model="healingCapComponentId"
              :disabled="!enableHealingCap"
              label="Healing Cap Library for Export"
              :items="mappedHealingCaps"
              outlined
              dense
              hide-details
              class="mx-4"
              :menu-props="{ rounded: '10' }"
            />
          </v-col>
          <v-col cols="6">
            <template v-if="scanBodyMesh">
              <div class="ml-6 title-font">Scan Body</div>
              <ModelViewer
                :filename="scanBodyMesh.filename"
                :url="scanBodyMesh.url"
              />
            </template>
          </v-col>
          <v-col cols="6">
            <template v-if="healingCapMesh">
              <div class="ml-6 title-font">Healing Cap</div>
              <ModelViewer
                :filename="healingCapMesh.filename"
                :url="healingCapMesh.url"
              />
            </template>
          </v-col>
        </v-row>
      </v-card>
    </v-col>

    <v-col cols="12">
      <v-card flat class="rounded-20">
        <v-card-actions>
          <v-scale-transition origin="center center">
            <div
              class="flex-grow-1 ml-2 mr-3"
              v-if="loading"
              style="width: 100%"
            >
              <v-progress-linear
                rounded
                indeterminate
                color="primary"
                height="36"
                class="rounded-10"
                background-opacity="0.6"
              >
                <span
                  class="text-body-2 font-weight-medium white--text loading-pulse"
                >
                  PROCESSING...
                </span>
              </v-progress-linear>
            </div>
          </v-scale-transition>
          <v-spacer />
          <!-- <v-btn
            class="btn-primary white--text"
            :disabled="loading"
            small
            @click="associateToCase = true"
          >
            <v-icon class="mr-2">mdi-link-box-outline</v-icon>
            Associate to Case
          </v-btn> -->
          <v-btn
            class="btn-primary white--text"
            :disabled="!downloadEnabled || loading"
            @click="exportAbutment"
          >
            <v-icon class="mr-2">mdi-download</v-icon>
            Download
          </v-btn>
        </v-card-actions>
      </v-card>
      <AssociateToCase v-model="associateToCase" />
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import ModelViewer from "@/components/HomePage/MainCard/UploadScans/ScanUploader/ModelViewer.vue"
import AssociateToCase from "./AssociateToCase.vue"

export default {
  name: "ExportAbutment",

  components: { ModelViewer, AssociateToCase },

  data() {
    return {
      dialog: false,
      loading: false,
      enableHealingCap: false,
      scanBodyComponentId: null,
      healingCapComponentId: null,
      scanBodyMesh: null,
      healingCapMesh: null,
      // Perhaps move meshCache to store.
      // Perhaps fetch all meshes upfront?
      meshCache: [],
      acquisitionStrategy: null,
      exportUid: null,
      associateToCase: false
    }
  },

  async mounted() {
    if (!this.exportComponents) this.fetchExportComponents()
  },

  computed: {
    ...mapGetters(["exportComponents", "ncSessionID", "analyzerSession"]),

    mappedScanBodies() {
      if (!this.exportComponents) return []
      return this.exportComponents
        .filter((c) => c.type === "Scan Body")
        .map((c) => {
          return {
            text: c.name,
            value: c.id
          }
        })
    },

    mappedHealingCaps() {
      if (!this.exportComponents) return []
      return this.exportComponents
        .filter((c) => c.type === "Healing Cap")
        .map((c) => {
          return {
            text: c.name,
            value: c.id
          }
        })
    },

    downloadEnabled() {
      if (this.enableHealingCap)
        return this.scanBodyComponentId && this.healingCapComponentId
      else return !!this.scanBodyComponentId
    }
  },

  watch: {
    scanBodyComponentId(val) {
      if (val) {
        this.fetchExportMesh("scanBody")
      }
    },
    healingCapComponentId(val) {
      if (val) {
        this.fetchExportMesh("healingCap")
      }
    },
    enableHealingCap(val) {
      if (!val) {
        this.healingCapComponentId = null
        this.healingCapMesh = null
      }
    }
  },

  methods: {
    ...mapActions(["setExportComponents"]),

    async fetchExportComponents() {
      this.loading = true
      const response = await this.$axios.get(`/proxy/components/`)
      this.setExportComponents(response.data)
      this.loading = false
    },

    async fetchExportMesh(type) {
      const typeId = type + "ComponentId"
      const typeMesh = type + "Mesh"

      const cachedMesh = this.meshCache.find(
        (m) => m.component_id === this[typeId]
      )
      if (cachedMesh) return (this[typeMesh] = cachedMesh)

      this.loading = true
      const response = await this.$axios.get(
        `/proxy/components/${this[typeId]}/`
      )
      this[typeMesh] = response.data[0]
      this[typeMesh].component_id = this[typeId]
      this.meshCache.push(this[typeMesh])
      this.loading = false
    },

    async exportAbutment() {
      this.loading = true
      const payload = {
        nc_session_id: this.ncSessionID,
        scan_body_id: this.scanBodyComponentId
      }
      if (this.healingCapComponentId)
        payload.healing_cap_id = this.healingCapComponentId

      const exportsResponse = await this.$axios.post(
        "/proxy/components/download/",
        payload,
        {
          responseType: "blob"
        }
      )

      const url = window.URL.createObjectURL(new Blob([exportsResponse.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute(
        "download",
        `export_${this.analyzerSession.reference_number}.zip`
      )
      document.body.appendChild(link)
      link.click()

      link.remove()
      window.URL.revokeObjectURL(url)
      this.loading = false
    }
  }
}
</script>
